import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import PageHero from "@/components/PageHero";
import { MapPin, Clock, ExternalLink } from "lucide-react";

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function formatRange(startStr, endStr) {
  const s = parseDate(startStr);
  const e = parseDate(endStr);
  if (!s) return "";
  const fmt = (d) => `${String(d.getDate()).padStart(2, "0")} de ${d.toLocaleString("pt-BR", { month: "long" })} de ${d.getFullYear()}`;
  if (!e || e.toDateString() === s.toDateString()) return fmt(s);
  // Same month & year → "15 a 18 de nov de 2026"
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.getDate()} a ${e.getDate()} de ${s.toLocaleString("pt-BR", { month: "long" })} de ${s.getFullYear()}`;
  }
  // Same year → "28 de out a 3 de nov de 2026"
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} de ${s.toLocaleString("pt-BR", { month: "short" }).replace(".", "")} a ${e.getDate()} de ${e.toLocaleString("pt-BR", { month: "short" }).replace(".", "")} de ${s.getFullYear()}`;
  }
  // Cross-year → full
  return `${fmt(s)} — ${fmt(e)}`;
}

export default function Events() {
  const { settings } = useSettings();
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events?status=published").then((r) => setEvents(r.data)); }, []);

  return (
    <div className="bg-[color:var(--marupiara-ivory)]">
      <PageHero image={settings.events_hero_image} overline="Eventos" title="Bienais, feiras e lançamentos." />
      <section className="py-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 space-y-8">
          {events.map((e) => {
            const start = e.start_date || e.date;
            const range = formatRange(start, e.end_date);
            return (
              <div key={e.id} data-testid={`event-${e.id}`} className="grid md:grid-cols-3 gap-6 items-start p-6 bg-white border border-[color:var(--marupiara-gold)]/20 hover:border-[color:var(--marupiara-gold)] transition-colors">
                {e.image_url && <img src={e.image_url} alt={e.name} className="w-full aspect-[4/3] object-cover" />}
                <div className="md:col-span-2">
                  {range && <div className="overline text-[color:var(--marupiara-gold-dark)] mb-2" data-testid={`event-range-${e.id}`}>{range}</div>}
                  <h3 className="font-serif text-3xl text-[color:var(--marupiara-obsidian)] mb-3">{e.name}</h3>
                  <p className="text-neutral-700 leading-relaxed mb-4">{e.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-[color:var(--marupiara-text-on-light-muted)] mb-4">
                    {e.time && <span className="flex items-center gap-1"><Clock size={14} className="text-[color:var(--marupiara-gold-dark)]" /> {e.time}</span>}
                    {e.location && <span className="flex items-center gap-1"><MapPin size={14} className="text-[color:var(--marupiara-gold-dark)]" /> {e.location}</span>}
                  </div>
                  {e.registration_link && (
                    <a href={e.registration_link} target="_blank" rel="noreferrer" data-testid={`event-register-${e.id}`} className="btn-outline-gold text-sm border-[color:var(--marupiara-gold-dark)] text-[color:var(--marupiara-gold-dark)]">
                      Inscrever-se <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          {events.length === 0 && <div className="text-center py-16 text-[color:var(--marupiara-text-on-light-muted)]">Nenhum evento programado no momento.</div>}
        </div>
      </section>
    </div>
  );
}

export { formatRange };
